import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Check, ChevronLeft, ChevronRight, CircleAlert, CreditCard, Loader2 } from 'lucide-react';
import { Trans } from 'react-i18next';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { type PlanBalancePoint, PlanBalanceChart } from '@/components/payments/PlanBalanceChart';
import { PlanScheduleList } from '@/components/payments/PlanScheduleList';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { fromSubunits } from '@/lib/currency';
import { buildPlanSchedule, type PlanFrequency } from '@/lib/payment-schedule';

/**
 * The card is saved as a payment intent before any instalment is taken, so the bank shows a
 * zero-amount authorisation rather than the first instalment. Not a placeholder.
 */
const AUTHORISATION_AMOUNT = 0;

export interface PaymentPlanReviewData {
  startDate: Date;
  frequency: PlanFrequency;
  paymentCount: number;
}

interface PaymentPlanReviewStepProps {
  /** Discounted plan total the schedule is built from. */
  totalAmount: number;
  /**
   * The balance before the offer discount, shown struck through beside the total so the
   * commitment screen still states what is actually owed. Omitted when no discount applies.
   */
  originalAmount?: number;
  currency?: string;
  /** Creditor/client display name for the "informed of your payment" line. */
  clientName: string;
  plan: PaymentPlanReviewData;
  onEditPlan: () => void;
  onAuthorise: () => void;
  isProcessing: boolean;
  error: string | null;
}

export function PaymentPlanReviewStep({
  totalAmount,
  originalAmount,
  currency = 'EUR',
  clientName,
  plan,
  onEditPlan,
  onAuthorise,
  isProcessing,
  error,
}: PaymentPlanReviewStepProps) {
  const { t, currentLanguage } = useTranslation();

  const instalments = buildPlanSchedule(totalAmount, plan.paymentCount, plan.startDate, plan.frequency);
  const firstAmount = fromSubunits(instalments[0].subunits);
  const clearedDate = instalments[instalments.length - 1].date;

  const now = new Date();
  const isToday =
    plan.startDate.getDate() === now.getDate() && plan.startDate.getMonth() === now.getMonth() && plan.startDate.getFullYear() === now.getFullYear();

  // Null unless an offer actually reduced the balance, so one check narrows every use below.
  const balanceBeforeDiscount = originalAmount != null && originalAmount > totalAmount ? originalAmount : null;

  const dateFmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const clearedLabel = clearedDate.toLocaleDateString(currentLanguage, dateFmt);

  // Same shape the plan page charts once the plan exists: a starting point the month before
  // the first instalment, then the balance after each one. Every point is drawn on the solid
  // series so the customer sees the plan as it will look once it has run its course.
  const monthOf = (d: Date) => d.toLocaleDateString(currentLanguage, { month: 'short' });
  const monthBeforeStart = new Date(plan.startDate.getFullYear(), plan.startDate.getMonth() - 1, 1);
  const chartData: PlanBalancePoint[] = [
    { month: monthOf(monthBeforeStart), balance: totalAmount, predicted: totalAmount },
    ...instalments.map((i) => {
      const remaining = fromSubunits(i.balanceAfterSubunits);
      return { month: monthOf(i.date), balance: remaining, predicted: remaining };
    }),
  ];

  return (
    <div className="w-full h-full stack items-center mt-4 px-4">
      <div className="w-full max-w-lg mx-auto h-full flex flex-col space-y-6">
        <div className="flex gap-4 items-center">
          <Button
            variant="subtle"
            size="icon-xs"
            onClick={onEditPlan}
            disabled={isProcessing}
            className="rounded-full"
            aria-label={t('paymentPlan.review.editPlan')}
          >
            <ChevronLeft />
          </Button>
          <h2 className="text-lg font-semibold">{t('paymentPlan.review.title')}</h2>
        </div>

        {/* Plan summary + balance graphic */}
        <div className="bg-card rounded-3xl p-6 border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('paymentPlan.review.balanceToday')}</p>
              {balanceBeforeDiscount != null && (
                <CurrencyDisplay amount={balanceBeforeDiscount} currency={currency} className="text-sm line-through text-destructive" />
              )}
              <CurrencyDisplay amount={totalAmount} currency={currency} className="text-2xl font-bold" centsClassName="text-lg" />
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('paymentPlan.review.balanceCleared')}</p>
              <p className="text-sm font-semibold mt-0.5 text-green-600">{clearedLabel}</p>
            </div>
          </div>
          <div className="mt-3 mb-8 stack gap-x-4 gap-y-1 text-sm">
            <div className="flex justify-between gap-1 border-b border-border-light pb-2">
              <div className="text-muted-foreground">{t('paymentPlan.numberOfPayments')}</div>
              <div className="text-right font-medium tabular-nums">{plan.paymentCount}</div>
            </div>
            <div className="flex justify-between gap-1 border-b border-border-light pb-2">
              <div className="text-muted-foreground">{t('paymentPlan.frequency')}</div>
              <div className="text-right font-medium">{t(`paymentPlan.${plan.frequency}`)}</div>
            </div>
            <div className="flex justify-between gap-1 border-b border-border-light pb-2">
              <div className="text-muted-foreground">{t('paymentPlan.youllPay')}</div>
              <CurrencyDisplay amount={firstAmount} currency={currency} className="text-sm" />
            </div>
            <div className="flex justify-between gap-1">
              <div className="text-muted-foreground">{t('paymentPlan.firstPayment')}</div>
              {isToday ? t('paymentPlan.today') : plan.startDate.toLocaleDateString(currentLanguage, dateFmt)}
            </div>
          </div>
          {balanceBeforeDiscount != null && (
            <p className="text-sm font-medium mt-1">
              <Trans
                i18nKey="discount.youSaveAcrossPlan"
                components={{
                  amount: (
                    <CurrencyDisplay amount={balanceBeforeDiscount - totalAmount} currency={currency} className="font-semibold inline-flex gap-0" />
                  ),
                }}
              />
            </p>
          )}
          <div className="mt-3">
            <PlanBalanceChart data={chartData} currency={currency} className="h-40 w-full" />
            <div className="flex justify-between text-xs text-muted-foreground mt-3">
              <span className="stack items-center gap-1">
                <Badge variant="accent">{t('paymentPlan.review.today')}</Badge>
                <CurrencyDisplay amount={totalAmount} currency={currency} className="text-xs" />
              </span>
              <span className="stack items-center gap-1 text-green-600 font-medium">
                <Badge variant="success">{clearedLabel}</Badge>
                <CurrencyDisplay amount={0} currency={currency} className="text-xs" />
              </span>
            </div>
          </div>
        </div>

        {/* What happens — card mechanics, kept secondary */}
        <div className="space-y-4">
          <div className="flex gap-3 items-start">
            <div className="highlight-accent rounded-full p-2 shrink-0">
              <CreditCard className="size-4" />
            </div>
            <div>
              <p className="text-sm font-medium">{t('paymentPlan.review.authTitle')}</p>
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="paymentPlan.review.authSub"
                  components={{ amount: <CurrencyDisplay amount={AUTHORISATION_AMOUNT} currency={currency} className="inline-flex gap-0" /> }}
                />
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="highlight-accent rounded-full p-2 shrink-0">
              <Check className="size-4" />
            </div>
            <div>
              <p className="text-sm font-medium">
                {isToday
                  ? t('paymentPlan.review.clientInformedTitle', { client: clientName })
                  : t('paymentPlan.review.clientInformedTitleDeferred', {
                      client: clientName,
                      date: plan.startDate.toLocaleDateString(currentLanguage, dateFmt),
                    })}
              </p>
              <p className="text-sm text-muted-foreground">
                {isToday
                  ? t('paymentPlan.review.clientInformedSub', { client: clientName })
                  : t('paymentPlan.review.clientInformedSubDeferred', { client: clientName })}
              </p>
            </div>
          </div>
        </div>

        <PlanScheduleList instalments={instalments} currency={currency} totalAmount={totalAmount} startsToday={!!isToday} />

        {error && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <BottomActionBar>
        <Button onClick={onAuthorise} size="xl" className="group w-full max-w-sm" disabled={isProcessing}>
          {isProcessing ? (
            <>
              <Loader2 className="size-5 mr-2 animate-spin" />
              {t('verify.processing')}
            </>
          ) : (
            <>
              {/* Wrap the label so the button's flex gap sits before the chevron only —
                  not between "pay" and the amount that <Trans> would otherwise spill. */}
              <span>
                {isToday ? (
                  <Trans
                    i18nKey="paymentPlan.review.ctaToday"
                    components={{ amount: <CurrencyDisplay amount={firstAmount} currency={currency} className="inline-flex gap-0" /> }}
                  />
                ) : (
                  t('paymentPlan.review.ctaDeferred')
                )}
              </span>
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
